import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

export interface LanguageOption {
  value: string
  label: string
}

export const availableLanguages: LanguageOption[] = [
  { value: 'zh-CN', label: '中文' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'ru', label: 'Русский' },
  { value: 'pl', label: 'Polski' },
  { value: 'sv', label: 'Svenska' },
]

const countryToLang: Record<string, string> = {
  CN: 'zh-CN', TW: 'zh-CN', HK: 'zh-CN', MO: 'zh-CN',
  US: 'en', GB: 'en', AU: 'en', CA: 'en', NZ: 'en', IE: 'en',
  FR: 'fr', SN: 'fr', CI: 'fr', CM: 'fr', GA: 'fr',
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', CL: 'es', EC: 'es', VE: 'es', CU: 'es',
  IT: 'it', SM: 'it',
  PT: 'pt', BR: 'pt', AO: 'pt', MZ: 'pt',
  NL: 'nl',
  RU: 'ru', BY: 'ru', KZ: 'ru', UA: 'ru',
  PL: 'pl',
  SE: 'sv',
}

export async function detectLanguageFromIP(): Promise<string> {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)
    const res = await fetch('https://ipapi.co/json/', { signal: controller.signal })
    clearTimeout(timeoutId)
    if (!res.ok) return 'zh-CN'
    const data = (await res.json()) as Record<string, unknown>
    const country: string | undefined = data.country_code as string | undefined
    if (country && countryToLang[country]) {
      return countryToLang[country]
    }
    return 'zh-CN'
  } catch {
    return 'zh-CN'
  }
}

export function useLang() {
  const { locale } = useI18n()

  const currentLocale = ref(locale.value)

  function setLocale(newLocale: string) {
    locale.value = newLocale
    currentLocale.value = newLocale
    localStorage.setItem('locale', newLocale)
    document.documentElement.setAttribute('lang', newLocale)
  }

  function detectLocaleFromBrowser(): string {
    const browserLang = navigator.language
    const matched = availableLanguages.find((l) =>
      browserLang.startsWith(l.value) || l.value.startsWith(browserLang),
    )
    return matched ? matched.value : 'zh-CN'
  }

  async function getCurrentLanguage(): Promise<string> {
    const stored = localStorage.getItem('locale')
    if (stored) return stored

    const ipLang = await detectLanguageFromIP()
    if (ipLang !== 'zh-CN') return ipLang

    return detectLocaleFromBrowser()
  }

  async function initLocale() {
    const lang = await getCurrentLanguage()
    setLocale(lang)
  }

  return {
    availableLanguages,
    currentLocale,
    setLocale,
    detectLocaleFromBrowser,
    detectLanguageFromIP,
    getCurrentLanguage,
    initLocale,
  }
}
